import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import InventoryNavigation from 'components/inventory/InventoryNavigation';
import InventoryConditionsTable from 'components/settings/InventoryConditionsTable';
import InventoryConditionCreateEditDialog from 'components/settings/InventoryConditionCreateEditDialog';

import {
  fetchInventoryConditions,
  clearInventoryConditions,
} from 'redux/modules/inventoryConditions';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class Conditions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearInventoryConditions();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters.Name !== filters.Name;
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }

    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, inventoryConditions, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Name'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (inventoryConditions.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: 'contains',
        val: filters[key],
        case_sensitivity: false,
      }))
      : null;
    actions.fetchInventoryConditions({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onFilterButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.inventoryConditions.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const { inventoryConditions, count, loading, location, filters } = this.props;
    const { createDialogOpen, mobileFilterExpanded } = this.state;
    return (
      <Layout>
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Inventory Conditions
          </title>
        </Helmet>
        <InventoryNavigation location={location} />
        <Filter
          form="inventoryConditionsFilter"
          placeholder="Search Inventory Conditions"
          primaryButtonText="Add New"
          onPrimaryButtonClick={this.onFilterButtonClick}
          beforeTable
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={inventoryConditions}
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <InventoryConditionsTable
          items={inventoryConditions}
          count={count}
          loading={loading}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
        <InventoryConditionCreateEditDialog
          form="inventoryConditionCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={() => {
            this.onCreateDialogClose();
            this.onRequest();
          }}
        />
      </Layout>
    );
  }
}

Conditions.propTypes = {
  inventoryConditions: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    Name: PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchInventoryConditions: PropTypes.func.isRequired,
    clearInventoryConditions: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
};

const mapStateToProps = (store) => {
  const inventoryConditionsFilterValues = getFormValues('inventoryConditionsFilter')(store);
  return {
    inventoryConditions: store.inventoryConditions.data,
    count: store.inventoryConditions.count,
    filters: {
      ...inventoryConditionsFilterValues
        && inventoryConditionsFilterValues.Filter
        ? { Name: inventoryConditionsFilterValues.Filter }
        : {},
    },
    loading: store.inventoryConditions.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchInventoryConditions,
    clearInventoryConditions,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withWidth()(Conditions));



// WEBPACK FOOTER //
// ./src/pages/inventory/Conditions.js